import React, { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";

import {
  CfcDialog,
  CfcHeader,
  CfcHeaderTitle,
  CfcCloseButton,
  OrderModalLayout,
  StepNavigationSidebar,
  CfcOrderingStepsTitle,
  CfcStepList,
  CfcStepItem,
  StepCircle,
  OrderStepContentArea,
  ScrollableSection,
  CfcFooter,
  CfcFooterRight,
  CfcSubmitError,
} from "./CfcOrderModal.styles";

import { Button } from "../shared/Button/Button";
import { CfcStepReview } from "./CfcStepReview";
import { CfcStepSelect } from "./CfcStepSelect";
import { CfcStepAttestAndAuthorize } from "./CfcStepAttestAndAuthorize";
import { CfcConfirmation } from "./CfcConfirmation";
import { CfcReportPreviewDialog } from "./CfcReportPreviewDialog";
import { CfcStepProviderInfo } from "./CfcStepProviderInfo";
import { CFC_CONDITIONS } from "./cfcConditions";
import type { CfcEligibleCondition } from "./cfcConditions";
import {
  CfcFormData,
  CfcOrderStep,
  CfcPatientData,
  initialCfcFormData,
} from "./types";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { SubmitCfcOrder } from "./Services/CfcService";
import { getOnlineOrderPrintout } from "../NewRequisition/Services/NewReqService";
import { printPdfFromBlob } from "../../utils/printUtils";
import { SendActionType } from "../shared/Services/SharedServices";
import { isCfcCollectionDateAfterDeadline } from "./cfcCollectionDate";

interface CfcOrderModalProps {
  open: boolean;
  onClose: () => void;
  patientData: CfcPatientData;
  onOrderSubmitted?: (reqIdentifier: string) => void;
  canSubmitOrder?: boolean;
}

interface CfcConfirmedViewProps {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  patientData: CfcPatientData;
}

const STEPS: { key: CfcOrderStep; label: string }[] = [
  { key: "provider", label: "Add Provider Info" },
  { key: "review", label: "Review Patient Details" },
  { key: "select", label: "Confirm Selections" },
  { key: "attestAuthorize", label: "Attest & Authorize Test" },
];
const CFC_ORDER_MODAL_TITLE = "NEW UNITY CONFIRM TEST";
const PRINT_ERROR_MESSAGE =
  "We couldn't download the order printout. Please try again.";
const SUBMIT_ERROR_MESSAGE = "Please try again or contact support.";
const DEFAULT_CFC_ORDER_NUMBER = "CFC-ORDER";
const PRINT_ACTION_TYPE = "ProviderOnlineOrderPrintModal";

const createInitialFormData = (patientData: CfcPatientData): CfcFormData => ({
  ...initialCfcFormData,
  selectedConditions: patientData.isCfcEligibilityOverridden
    ? []
    : patientData.eligibleConditions.map((condition) => condition.code),
});

const getSelectableConditions = (
  patientData: CfcPatientData,
): ReadonlyArray<CfcEligibleCondition> =>
  patientData.isCfcEligibilityOverridden
    ? CFC_CONDITIONS
    : patientData.eligibleConditions;

const isAttestAuthorizeComplete = (
  formData: CfcFormData,
  patientData: CfcPatientData,
): boolean =>
  formData.attestation1 &&
  formData.attestation2 &&
  (patientData.isCfcEligibilityOverridden || formData.attestation3) &&
  formData.providerAuthorization &&
  !isCfcCollectionDateAfterDeadline(
    formData.collectionDate,
    patientData.cfcEligibleCollectByDate,
  );

const isStepNextDisabled = (
  currentStep: CfcOrderStep,
  formData: CfcFormData,
  patientData: CfcPatientData,
) => {
  switch (currentStep) {
    case "provider":
      return (
        !formData.clinicAccountNumber ||
        !formData.orderingHealthcareNPI ||
        !formData.orderingHealthcareProviders
      );
    case "select":
      return (
        formData.selectedConditions.length === 0 ||
        (!formData.icd10Abnormal && !formData.icd10OtherCodes.trim())
      );
    case "attestAuthorize":
      return !isAttestAuthorizeComplete(formData, patientData);
    default:
      return false;
  }
};

const getCfcPrintoutFilename = (patientData: CfcPatientData): string =>
  `${patientData.firstName?.slice(0, 1) || "P"}${
    patientData.lastName?.slice(0, 1) || "P"
  }_cfc_ordered_report.pdf`;

const CfcConfirmedView = ({
  open,
  onClose,
  orderNumber,
  patientData,
}: CfcConfirmedViewProps): React.ReactElement => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { physicalDeviceType } = useDeviceHook();

  const [isPrinting, setIsPrinting] = useState(false);
  const [printError, setPrintError] = useState<string>("");

  const handlePrint = async () => {
    if (!auth) return;
    setPrintError("");
    setIsPrinting(true);

    try {
      const pdfUrl = await getOnlineOrderPrintout(
        auth.token,
        orderNumber,
        setLoggedOut,
      );

      if (!pdfUrl) throw new Error("No PDF URL returned");

      SendActionType(PRINT_ACTION_TYPE, setLoggedOut, auth.token).catch(
        () => undefined,
      );

      const pdfResponse = await fetch(pdfUrl, {
        method: "GET",
        headers: { "Content-Type": "application/pdf" },
      });

      if (!pdfResponse.ok) throw new Error("Failed to download PDF");

      const blob = await pdfResponse.blob();
      const filename = getCfcPrintoutFilename(patientData);

      await printPdfFromBlob(blob, filename, physicalDeviceType);
    } catch {
      setPrintError(PRINT_ERROR_MESSAGE);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <CfcDialog
      $isConfirmationView
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <CfcHeader>
        <CfcHeaderTitle>{CFC_ORDER_MODAL_TITLE}</CfcHeaderTitle>
        <CfcCloseButton onClick={onClose} aria-label="Close modal">
          <FontAwesomeIcon icon={faXmark} />
        </CfcCloseButton>
      </CfcHeader>
      <ScrollableSection>
        <CfcConfirmation orderNumber={orderNumber} />
      </ScrollableSection>
      <CfcFooter $isConfirmationView>
        <CfcFooterRight>
          <Button label="CLOSE" onClick={onClose} size="medium" reverse />
          {printError && <CfcSubmitError>{printError}</CfcSubmitError>}
          <Button
            label="PRINT"
            onClick={handlePrint}
            size="medium"
            loading={isPrinting}
          />
        </CfcFooterRight>
      </CfcFooter>
    </CfcDialog>
  );
};

const CfcOrderModalSession = ({
  open,
  onClose,
  patientData,
  onOrderSubmitted,
  canSubmitOrder = true,
}: CfcOrderModalProps): React.ReactElement => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const stepContentRef = useRef<HTMLElement | null>(null);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState<CfcFormData>(() =>
    createInitialFormData(patientData),
  );

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isReportPreviewOpen, setIsReportPreviewOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");

  const currentStep = STEPS[currentStepIndex]?.key ?? "provider";
  const isSubmitStep = currentStepIndex === STEPS.length - 1;
  const isNextDisabled = isStepNextDisabled(currentStep, formData, patientData);
  const isSubmitDisabled = isSubmitStep && !canSubmitOrder;

  useLayoutEffect(() => {
    if (!stepContentRef.current) return;
    stepContentRef.current.scrollTop = 0;
    stepContentRef.current.scrollLeft = 0;
  }, [currentStepIndex]);

  const handleFormChange = (updates: Partial<CfcFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleClose = () => {
    setCurrentStepIndex(0);
    setFormData(createInitialFormData(patientData));
    setIsConfirmed(false);
    setIsReportPreviewOpen(false);
    setOrderNumber("");
    setSubmitError("");
    onClose();
  };

  const handleCloseReportPreview = () => {
    setIsReportPreviewOpen(false);
  };

  const handleNext = async () => {
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
      return;
    }

    if (!auth || !canSubmitOrder) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await SubmitCfcOrder(
        auth.token,
        {
          source_req_identifier: patientData.reqId,
          lims_id: formData.clinicAccountNumber,
          npi: formData.orderingHealthcareNPI,
          collection_date: formData.collectionDate || undefined,
          selected_conditions: formData.selectedConditions,
          is_O28_5_aneuploidy: formData.icd10Abnormal,
          other_aneuploidy_icd_10_codes: formData.icd10OtherCodes.trim(),
          is_mobile_phlebotomy: formData.isMobilePhlebotomy,
        },
        setLoggedOut,
      );

      if (response.ok) {
        const data = await response.json();
        setOrderNumber(data.unity_complete_id || DEFAULT_CFC_ORDER_NUMBER);
        onOrderSubmitted?.(patientData.reqId);
        setIsConfirmed(true);
      } else {
        throw new Error("Failed to submit order");
      }
    } catch {
      setSubmitError(SUBMIT_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
      setSubmitError("");
    }
  };

  if (isConfirmed) {
    return (
      <CfcConfirmedView
        open={open}
        onClose={handleClose}
        orderNumber={orderNumber}
        patientData={patientData}
      />
    );
  }

  if (isReportPreviewOpen) {
    return (
      <CfcReportPreviewDialog
        open={open}
        reportId={patientData.aneuploidyReportId}
        onBack={handleCloseReportPreview}
        onClose={handleCloseReportPreview}
      />
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case "provider":
        return (
          <CfcStepProviderInfo
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case "review":
        return (
          <CfcStepReview
            patientData={patientData}
            onReviewReport={() => setIsReportPreviewOpen(true)}
          />
        );
      case "select":
        return (
          <CfcStepSelect
            conditions={getSelectableConditions(patientData)}
            formData={formData}
            onFormChange={handleFormChange}
          />
        );
      case "attestAuthorize":
        return (
          <CfcStepAttestAndAuthorize
            cfcEligibleCollectByDate={patientData.cfcEligibleCollectByDate}
            formData={formData}
            isComplete={isAttestAuthorizeComplete(formData, patientData)}
            onFormChange={handleFormChange}
            providerName={
              formData.orderingHealthcareProviders ||
              auth?.default_provider_info?.default_provider_name ||
              ""
            }
            providerNpi={
              formData.orderingHealthcareNPI ||
              auth?.default_provider_info?.default_npi ||
              ""
            }
          />
        );
      default:
        return null;
    }
  };

  const footerActionButton = (
    <Button
      label={isSubmitStep ? "SUBMIT ORDER" : "NEXT"}
      onClick={handleNext}
      size="medium"
      disabled={isNextDisabled || isSubmitDisabled}
      loading={isSubmitting}
    />
  );

  return (
    <CfcDialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <CfcHeader>
        <CfcHeaderTitle>{CFC_ORDER_MODAL_TITLE}</CfcHeaderTitle>
        <CfcCloseButton onClick={handleClose} aria-label="Close modal">
          <FontAwesomeIcon icon={faXmark} />
        </CfcCloseButton>
      </CfcHeader>

      <OrderModalLayout>
        <StepNavigationSidebar aria-label="Ordering steps navigation">
          <CfcOrderingStepsTitle>Ordering Steps</CfcOrderingStepsTitle>
          <CfcStepList>
            {STEPS.map((step, index) => (
              <CfcStepItem
                key={step.key}
                $active={index === currentStepIndex}
                aria-current={index === currentStepIndex ? "step" : undefined}
              >
                <StepCircle $active={index === currentStepIndex}>
                  <FontAwesomeIcon
                    icon={index < currentStepIndex ? faCircleCheck : faCircle}
                  />
                </StepCircle>
                {step.label}
              </CfcStepItem>
            ))}
          </CfcStepList>
        </StepNavigationSidebar>

        <OrderStepContentArea>
          <ScrollableSection
            ref={stepContentRef}
            aria-label="Current order step content"
          >
            {renderStepContent()}
          </ScrollableSection>

          <CfcFooter>
            {currentStepIndex === 0 ? (
              <Button
                label="CANCEL"
                onClick={handleClose}
                size="medium"
                reverse
              />
            ) : (
              <Button label="BACK" onClick={handleBack} size="medium" reverse />
            )}
            <CfcFooterRight>
              {submitError && <CfcSubmitError>{submitError}</CfcSubmitError>}
              {isSubmitDisabled ? (
                <Tooltip arrow title="Sales rep users cannot place orders.">
                  <span>{footerActionButton}</span>
                </Tooltip>
              ) : (
                footerActionButton
              )}
            </CfcFooterRight>
          </CfcFooter>
        </OrderStepContentArea>
      </OrderModalLayout>
    </CfcDialog>
  );
};

export const CfcOrderModal = (
  props: CfcOrderModalProps,
): React.ReactElement | null => {
  if (!props.open) {
    return null;
  }

  return (
    <CfcOrderModalSession
      key={`${props.patientData.reqId}-${props.patientData.sfId}`}
      {...props}
    />
  );
};
